import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link, withRouter } from 'react-router-dom'
import { withAuth } from '../AuthContext'

import {
  PrimaryButton,
  PrimaryOutlineButton,
  BrushStoke,
} from '../Utils/CustomClasses'

import {
  Title,
  Subtitle,
  CustomSubtitle,
  Button,
  FlexRow,
} from '../Utils/CustomClassesV2'

//BASE CSS CODE
const UserContainer = styled.div`
  background-color: #fffbf4;
  box-shadow: inset 0px -150px 0px -12px rgba(255, 255, 255, 1);
  padding: 50px 0 50px 5vw;

  @media screen and (max-width: 980px) {
    padding: 50px 5vw;
  }
`

const CustomFlexRow = styled(FlexRow)`
  > div:first-child {
    width: auto;
  }
  > div:last-child {
    width: -webkit-fill-available !important;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`

const CustomCenterFlexRow = styled(FlexRow)`
  align-items: center;

  @media screen and (max-width: 980px) {
    width: -webkit-fill-available;
    justify-content: center;
    gap: 10vw;
  }

  @media screen and (max-width: 600px) {
    flex-wrap: wrap;
    gap: 15px;
    flex-direction: column;
    align-items: flex-start;
  }
`

const CenterFlexRow = styled(FlexRow)`
  align-items: center;

  @media screen and (max-width: 980px) {
    margin-top: 20px;
    flex-wrap: nowrap;
  }

  @media screen and (max-width: 720px) {
    margin-top: 20px;
    flex-wrap: wrap;
  }
`

//USER HEADER BASE CSS CODE
const UserPhoto = styled.div`
  img {
    width: 240px;
    height: 240px;
    object-fit: cover;
    object-position: center;
  }

  @media screen and (max-width: 980px) {
    width: -webkit-fill-available !important;
    text-align: center;
  }
`

const CustomTitle = styled(Title)`
  @media screen and (max-width: 980px) {
    width: -webkit-fill-available !important;
    text-align: center;
    margin-bottom: 20px;
  }
`

const UserInformation = styled.div``

const CustomPrimaryOutlineButton = styled(PrimaryOutlineButton)`
  padding: 12px 20px;
  border-color: #000;
  a {
    color: #000 !important;
    font-size: 20px;
  }
`

const UserHeader = ({ user }) => {
  const userName = user !== null ? user.full_name : 'User Name'
  const profileStrength = user !== null ? 70 : 0

  const BoxInfo = ({ icon, title, info }) => {
    return (
      <BoxInfoContainer>
        <div className="icon-box">
          <img src={icon} />
        </div>
        <div className="info-box">
          <Title size="18px" sizemd="16px" sizesm="18px">
            {title}
          </Title>
          <Subtitle color="#908E8E" size="18px" sizemd="16px" sizesm="18px">
            {info}
          </Subtitle>
        </div>
      </BoxInfoContainer>
    )
  }

  return (
    <UserContainer>
      <CustomFlexRow gap="30px">
        <UserPhoto>
          {user !== null && user.profilePhotoStatus === 'completed' ? (
            <img
              src={s3AwsEndpoint + '/' + user.profilePhotoO}
              onError={(e) => {
                e.target.onError = null
                e.target.src = '/images/listing/default-profile.png'
              }}
            />
          ) : (
            <img
              className="img-fluid"
              src={'/images/listing/default-profile.png'}
            />
          )}
        </UserPhoto>
        <div>
          <div>
            <CustomTitle
              size="35px"
              sizemd="2rem"
              sizesm="23px"
              margin="20px 0 0 0"
            >
              {userName}
            </CustomTitle>
            <CustomCenterFlexRow gap="150px">
              <ProfileStrengthBar profileStrength={profileStrength} />
              <Link to="/search?seeking=locals">
                <CustomPrimaryOutlineButton>
                  <a>{'Improve profile strength'}</a>
                </CustomPrimaryOutlineButton>
              </Link>
            </CustomCenterFlexRow>
          </div>
          <CenterFlexRow>
            <BoxInfo
              icon="/images/icons/location-circle.svg"
              title="London, United Kingdom"
              info="Location"
            />
            <BoxInfo
              icon="/images/icons/world-circle.svg"
              title="English, French"
              info="Speak languages"
            />
            <BoxInfo
              icon="/images/icons/people-circle.svg"
              title="Host, Traveller & Local"
              info="Account"
            />
          </CenterFlexRow>
        </div>
      </CustomFlexRow>
    </UserContainer>
  )
}

//PROGRESS BAR STRENGHT BASE CSS CODE
const ProgressBar = styled.div`
  max-width: 250px;
  .progress {
    background-color: #f6f0e6;
    height: 9px;
  }

  .progress-bar {
    background-color: #0bac5d;
  }

  @media screen and (max-width: 600px) {
    max-width: 100%;
  }
`

const ProfileStrengthBar = ({ profileStrength }) => {
  return (
    <ProgressBar>
      <div className="progress">
        <div
          className="progress-bar"
          role="progressbar"
          style={{ width: `${profileStrength}%` }}
          aria-valuenow={profileStrength}
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
      <CustomSubtitle
        mediumBold
        size="21px"
        sizemd="1rem"
        sizesm="20px"
        margin="10px 0 0 0"
        dangerouslySetInnerHTML={{
          __html: `Profile Strength: ${profileStrength}%`,
        }}
      />
    </ProgressBar>
  )
}

//INFO BOXES BASE CSS CODE
const BoxInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;

  > div {
    width: auto;
  }

  .icon-box {
    width: 62px;
    height: 62px;
    border-radius: 5px;
    border: 1px solid #dbd5d6;
    display: flex;
    align-items: center;
    justify-content: center;

    @media screen and (max-width: 600px) {
      flex-wrap: wrap;
      flex-direction: column;
    }
  }

  .info-box {
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media screen and (max-width: 600px) {
      flex-wrap: wrap;
      flex-direction: column;
    }
  }
`

const InfoBoxes = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 10px;
  padding: 20px 30px;
  background-color: #ffffff;

  @media screen and (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`

const InfoBoxContainer = styled.div`
  padding: 20px 0;
  border: 1.3px solid #ede8e9;
  margin-bottom: 20px;

  > h1 {
    text-transform: uppercase;
    padding: 0 0 10px 20px;
    border-bottom: 11px solid
      ${(props) => (props.colorBar ? props.colorBar : '#ECF2F3')};
  }

  .content {
    padding: 10px 30px 20px 30px;

    > :not(:last-child) {
      margin-bottom: 30px;
    }
  }
`

const InfoBox = ({ title, colorBar, children }) => {
  return (
    <InfoBoxContainer colorBar={colorBar}>
      <Title
        size="20px"
        sizemd="20px"
        sizesm="20x"
        margin="0 0 10px 0"
        weight="200"
      >
        {title}
      </Title>
      <div className="content">{children}</div>
    </InfoBoxContainer>
  )
}

const EventDatesComponent = ({ date, children }) => {
  return (
    <div>
      <Title
        color="#0BAC5D"
        size="20px"
        sizemd="20px"
        sizesm="20x"
        margin="0 0 10px 0"
      >
        {date}
      </Title>
      {children}
    </div>
  )
}

const MatchingProfilesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  .image-user {
    margin-right: 2vw;

    img {
      width: 100px;
      height: 100px;
      overflow: hidden;
      object-fit: cover;
      object-position: center;
    }
  }

  .info-user {
    width: -webkit-fill-available;
  }

  .action-user {
    width: -webkit-fill-available;
    text-align: end;
    button {
      min-width: 100px;
    }
  }

  @media screen and (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;

    .image-user,
    .info-user,
    .action-user {
      text-align: left !important;
    }

    .image-user {
      margin-right: 0%;
      margin-bottom: 2vw;
    }
  }
`

const MatchingProfiles = ({ photo, name, location, profile }) => {
  return (
    <MatchingProfilesContainer>
      <div className="image-user">
        <img
          src={s3AwsEndpoint + '/' + photo}
          onError={(e) => {
            e.target.onError = null
            e.target.src = '/images/listing/default-profile.png'
          }}
        />
      </div>
      <div className="info-user">
        <Title size="20px" sizemd="20px" sizesm="20x" margin="0 0 10px 0">
          {name}
        </Title>
        <Subtitle size="20px" sizemd="20px" sizesm="20x" margin="0 0 10px 0">
          {location}
        </Subtitle>
      </div>
      <div className="action-user">
        <Link to="/search?seeking=locals">
          <CustomPrimaryOutlineButton>
            <a>{'View Profile'}</a>
          </CustomPrimaryOutlineButton>
        </Link>
      </div>
    </MatchingProfilesContainer>
  )
}

const TrendingPostsContainer = styled.div`
  border-bottom: 1px solid #ede8e9;
  &:first-of-type {
    margin-top: 10px;
  }

  h2 {
    text-transform: uppercase;
  }
`

const CustomBold = styled.b`
  color: ${(props) => (props.color ? props.color : '#000')};
`

const TrendingPosts = ({ title, category, color }) => {
  return (
    <TrendingPostsContainer>
      <Title size="20px" sizemd="20px" sizesm="20x" margin="0 0 20px 0">
        {title}
      </Title>
      <Subtitle
        color="#ADADAD"
        size="16px"
        sizemd="16px"
        sizesm="16x"
        margin="0 0 15px 0"
      >
        Category:<CustomBold color={color}> {category} </CustomBold>
      </Subtitle>
    </TrendingPostsContainer>
  )
}
//MAIN COMPONENT
const CircleProfile = ({ auth }) => {
  let { loggedIn, user } = auth

  const [isloggedin, setIsLoggedIn] = useState(loggedIn)
  const [userData, setUserData] = useState(user)

  useEffect(() => {
    // console.log('AUTH: ', auth)
    // console.log('loggedIn: ', loggedIn);

    let { loggedIn, user } = auth
    setIsLoggedIn(loggedIn)
    loggedIn && setUserData(user)
  }, [auth])

  return (
    <div>
      <UserHeader user={user} />
      <InfoBoxes>
        <InfoBox title="upcoming events" colorBar="#ECF2F3">
          <EventDatesComponent date="3/03:">
            <CustomSubtitle
              size="20px"
              sizemd="20px"
              sizesm="20x"
              margin="0 0 10px 0"
            >
              <b>Dublin:</b> Dublin Mythology & Folklore @ 5:00 PM
            </CustomSubtitle>
            <CustomSubtitle
              size="20px"
              sizemd="20px"
              sizesm="20x"
              margin="0 0 10px 0"
            >
              <b>London:</b> Jack the Ripper Walking Tour @ 7:00 PM
            </CustomSubtitle>
            <CustomSubtitle
              size="20px"
              sizemd="20px"
              sizesm="20x"
              margin="0 0 10px 0"
            >
              <b>Lisbon:</b> The Unforgettable Lisbon City Center Tour @ 10:30
              AM
            </CustomSubtitle>
          </EventDatesComponent>
          <EventDatesComponent date="7/03:">
            <CustomSubtitle
              size="20px"
              sizemd="20px"
              sizesm="20x"
              margin="0 0 10px 0"
            >
              <b>London:</b> Jack the Ripper Walking Tour @ 7:00 PM
            </CustomSubtitle>
            <CustomSubtitle
              size="20px"
              sizemd="20px"
              sizesm="20x"
              margin="0 0 10px 0"
            >
              <b>Dublin:</b> Dublin Mythology & Folklore @ 5:00 PM
            </CustomSubtitle>
          </EventDatesComponent>
        </InfoBox>
        <div>
          <InfoBox title="Matching profiles" colorBar="#FFF6E1">
            <MatchingProfiles
              photo=""
              name="John Doe"
              location="London, United Kingdom"
              profile={'/'}
            />
            <MatchingProfiles
              photo=""
              name="Charlotte Lorenburg"
              location="Manchester, United Kingdom"
              profile={'/'}
            />
            <MatchingProfiles
              photo=""
              name="John Doe"
              location="London, United Kingdom"
              profile={'/'}
            />
          </InfoBox>
          <InfoBox title="Trending posts" colorBar="#E8F6EB">
            <TrendingPosts
              title="The best places to visit in London"
              category="Travel"
              color="#00BACC"
            />
            <TrendingPosts
              title="The best places to visit in London"
              category="Life"
              color="#FFC02A"
            />
            <TrendingPosts
              title="The best places to visit in London"
              category="Happy"
              color="#0BAC5D"
            />
            <TrendingPosts
              title="The best places to visit in London"
              category="Weird"
              color="#EB5E55"
            />
          </InfoBox>
        </div>
      </InfoBoxes>
    </div>
  )
}

export default withAuth(CircleProfile)
